import { BehaviorSubject, Observable, Subscription, first } from 'rxjs';
import { colisionDescriptorsToBoundingBox } from 'src/app/utils/Utils';
import { Box3, BoxGeometry, Mesh, MeshPhysicalMaterial, Object3D, Vector3 } from 'three';
import { GLTF } from 'three/examples/jsm/loaders/GLTFLoader';
import { statusEnum } from '../../enums/ProductEnums';
import { CollisionDescriptorType, CollisionDescriptorUsageType } from '../../interfaces/GenericInterfaces';
import { removeObjectsWithChildren } from '../../utils';
import { MovementDataDTO } from '../DTOs/MovementDataDTO';
import { ProductBlueprintDTO, ReceiptSettings } from '../DTOs/ProductBlueprintDTO';
import { Attribute, ProductBlueprintAttributePair } from './Attribute';
import { AttributeValue } from './AttributeValue';
import { CollisionDescriptor, OBBCollisionDescriptor } from './CollisionDescriptor';
import { FlippingData } from './FlippingData';
import { LocalizedText } from './LocalizedText';
import { MovementData } from './MovementData';
import { SCALAR_SMALLER_BOX } from './Product';
import { ProductBlueprintVisibility } from './ProductBlueprintVisibility';
import { ProductRelationshipGroup } from './ProductRelationshipGroup';
import { TransformationData } from './TransformationData';

const GENERATE_DEFAULT_COLISION_DESCRIPTOR = true;

export interface IdMapper {
  attributeMap: Map<string, ProductBlueprintAttributePair>;
  attributeValueMap: Map<string, AttributeValue>;
  productBlueprintMap: Map<string, ProductBlueprint>;
}

export function getEmptyIdMapper() {
  return {
    attributeMap: new Map<string, ProductBlueprintAttributePair>(),
    attributeValueMap: new Map<string, AttributeValue>(),
    productBlueprintMap: new Map<string, ProductBlueprint>(),
  } as IdMapper;
}

export class ProductBlueprint {
  private static _placeholderObject = (() => {
    const object = new Object3D();
    object.userData = {
      isPlaceholder: true,
    };

    object.add(new Mesh(new BoxGeometry(0.3, 0.3, 0.3), new MeshPhysicalMaterial({ color: 0xcccccc })));

    return object;
  })();

  public static fromDTO(productBlueprintDTO: ProductBlueprintDTO | null) {
    if (!productBlueprintDTO) {
      return null;
    }

    const attributes: Attribute[] = [];
    if (productBlueprintDTO.attributes) {
      for (const attributeDTO of productBlueprintDTO.attributes) {
        attributes.push(Attribute.fromDTO(attributeDTO));
      }
    }

    const productRelationshipGroups: ProductRelationshipGroup[] = [];
    if (productBlueprintDTO.productRelationshipGroups) {
      for (const productRelationshipGroupDTO of productBlueprintDTO.productRelationshipGroups) {
        productRelationshipGroups.push(ProductRelationshipGroup.fromDTO(productRelationshipGroupDTO));
      }
    }

    if (!productBlueprintDTO.movementData) {
      productBlueprintDTO.movementData = MovementDataDTO.defaultValue;
    }

    let collisionDescriptors = undefined;
    if (productBlueprintDTO.collisionDescriptors && productBlueprintDTO.collisionDescriptors.length) {
      collisionDescriptors = [];
      for (const collisionDescriptorDTO of productBlueprintDTO.collisionDescriptors) {
        collisionDescriptors.push(CollisionDescriptor.fromDTO(collisionDescriptorDTO));
      }
    }

    if (productBlueprintDTO.rotateStep) {
      productBlueprintDTO.rotateStep = (productBlueprintDTO.rotateStep * Math.PI) / 180; // transform to radians
    }
    if (productBlueprintDTO.possibleRotations) {
      productBlueprintDTO.possibleRotations = productBlueprintDTO.possibleRotations.map(
        // transform to radians
        (rotation) => (rotation * Math.PI) / 180,
      );
    }

    const utilityIdsString: string[] | undefined = productBlueprintDTO.utilityIds?.map(String);

    return new ProductBlueprint(
      productBlueprintDTO.id?.toString(),
      productBlueprintDTO.name?.map((localizedText) => LocalizedText.fromDTO(localizedText)),
      productBlueprintDTO.dimensions,
      productBlueprintDTO.modelUrl,
      productBlueprintDTO.thumbnailUrl,
      productBlueprintDTO.infoText?.map((localizedText) => LocalizedText.fromDTO(localizedText)),
      attributes,
      productRelationshipGroups,
      productBlueprintDTO.externalId,
      productBlueprintDTO.flippingAxis,
      productBlueprintDTO.basePrice,
      MovementData.fromDTO(productBlueprintDTO.movementData),
      productBlueprintDTO.tlorisPlaceholderIcon,
      collisionDescriptors,
      productBlueprintDTO.rotateStep,
      productBlueprintDTO.possibleRotations,
      productBlueprintDTO.builderName,
      productBlueprintDTO.jsonData,
      utilityIdsString,
      productBlueprintDTO.parameters,
      productBlueprintDTO.status,
      productBlueprintDTO.receiptSettings,
      productBlueprintDTO.productBlueprintVisibility
        ? ProductBlueprintVisibility.fromDTO(productBlueprintDTO.productBlueprintVisibility)
        : null,
    );
  }

  private _id?: string;
  private _dimensions: number[] | null;
  private _model: BehaviorSubject<Object3D | null>;
  private _model$: Observable<Object3D | null>;
  private _modelUrl: string | null;
  private _thumbnailUrl: string;
  private _tlorisPlaceholderIcon?: string | undefined;
  private _infoText: LocalizedText[];
  private _basePrice: number;
  protected _attributes: Attribute[];
  private _productRelationshipGroups: ProductRelationshipGroup[];
  private _externalId: string;
  private _flippingAxis: FlippingData | null;
  private _name: LocalizedText[];
  private _builderName: string | undefined;

  private _sizeVector = new Vector3();
  private _boundingBox: Box3 = new Box3();
  private _selfBoundingBox: Box3 = new Box3();
  private _centerOffset?: Vector3;
  private _rotateStep?: number | undefined;
  private _possibleRotations?: number[] | undefined;

  private _movementData: MovementData;
  private _collisionDescriptors: CollisionDescriptor[];
  private _smallerCollisionDescriptors: CollisionDescriptor[];
  private _modelLoadingSubscription?: Subscription;
  private _utilityIds?: string[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private _parameters: any;

  private _defaultColisionDescriptor?: OBBCollisionDescriptor;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private _jsonData: any;

  private _status?: statusEnum;

  private _receiptSettings?: ReceiptSettings;

  private _productBlueprintVisibility: ProductBlueprintVisibility | null;

  public get productBlueprintVisibility() {
    return this._productBlueprintVisibility;
  }

  public set productBlueprintVisibility(val: ProductBlueprintVisibility | null) {
    this._productBlueprintVisibility = val;
  }

  public get receiptSettings() {
    return this._receiptSettings;
  }

  public get status() {
    return this._status;
  }

  public get builderName(): string | undefined {
    return this._builderName;
  }

  public set builderName(value: string | undefined) {
    this._builderName = value;
  }

  public get centerOffset() {
    return this._centerOffset;
  }

  public get attributes() {
    return this._attributes;
  }

  public get id() {
    return this._id;
  }

  public get model$() {
    return this._model$;
  }

  public get modelUrl() {
    return this._modelUrl;
  }

  public set modelUrl(url: string | null) {
    this._modelUrl = url;
  }

  public get productRelationshipGroups() {
    return this._productRelationshipGroups;
  }

  public get externalId() {
    return this._externalId;
  }

  public set externalId(id: string) {
    this._externalId = id;
  }

  public get basePrice() {
    return this._basePrice;
  }

  public set basePrice(price: number) {
    this._basePrice = price;
  }

  public get thumbnailUrl() {
    return this._thumbnailUrl;
  }

  public set thumbnailUrl(url: string) {
    this._thumbnailUrl = url;
  }

  public get tlorisPlaceholderIcon(): string | undefined {
    return this._tlorisPlaceholderIcon;
  }

  public set tlorisPlaceholderIcon(value: string | undefined) {
    this._tlorisPlaceholderIcon = value;
  }

  public get dimensions() {
    return this._dimensions;
  }

  public set dimensions(data: number[] | null) {
    this._dimensions = data;
  }

  public get infoText() {
    return this._infoText;
  }

  public set infoText(text: LocalizedText[]) {
    this._infoText = text;
  }

  public get name() {
    return this._name;
  }

  public set name(text: LocalizedText[]) {
    this._name = text;
  }

  public get flippingAxis() {
    return this._flippingAxis;
  }

  public set flippingAxis(data: FlippingData | null) {
    this._flippingAxis = data;
  }

  public get boundingBox() {
    return this._boundingBox.clone();
  }

  public get selfBoundingBox() {
    return this._selfBoundingBox.clone();
  }

  public get movementData() {
    return this._movementData;
  }

  public get rotateStep(): number | undefined {
    return this._rotateStep;
  }

  public set rotateStep(value: number | undefined) {
    this._rotateStep = value;
  }

  public get possibleRotations(): number[] | undefined {
    return this._possibleRotations;
  }

  public get collisionDescriptors() {
    return this._collisionDescriptors;
  }

  public get smallerCollisionDescriptors() {
    return this._smallerCollisionDescriptors;
  }

  public get isBuilder() {
    return this.builderName !== undefined && this.builderName !== '';
  }

  public get jsonData() {
    return this._jsonData;
  }

  public get utilityIds() {
    return this._utilityIds;
  }

  public get parameters() {
    return this._parameters;
  }

  constructor(
    id: string | undefined,
    name: LocalizedText[],
    dimensions: number[] | null = null,
    modelUrl: string | null,
    thumbnailUrl: string,
    infoText: LocalizedText[],
    attributes: Attribute[] = [],
    productRelationshipGroups: ProductRelationshipGroup[] = [],
    externalId: string,
    flippingAxis: FlippingData | null = null,
    basePrice: number,
    movementData: MovementData,
    tlorisPlaceholderIcon?: string,
    collisionDescriptors: CollisionDescriptor[] = [],
    rotateStep?: number,
    possibleRotations?: number[],
    builderName?: string,
    jsonData?: string,
    utilityIds?: string[],
    parameters?: string,
    status?: statusEnum,
    receiptSettings?: ReceiptSettings,
    productBlueprintVisibility: ProductBlueprintVisibility | null = null,
  ) {
    this._id = id;
    this._name = name;
    this._dimensions = dimensions;
    this._infoText = infoText;
    this._attributes = attributes;
    this._productRelationshipGroups = productRelationshipGroups;
    this._externalId = externalId;
    this._flippingAxis = flippingAxis;
    this._modelUrl = modelUrl;
    this._thumbnailUrl = thumbnailUrl;
    this._tlorisPlaceholderIcon = tlorisPlaceholderIcon;
    const tempModel = this._modelUrl ? ProductBlueprint._placeholderObject : null;
    this._model = new BehaviorSubject<Object3D | null>(tempModel);
    if (tempModel) {
      this._setBoundingBox(tempModel);
    }
    this._model$ = this._model.asObservable();
    this._basePrice = basePrice;
    this._movementData = movementData;
    this._collisionDescriptors = collisionDescriptors;
    this._smallerCollisionDescriptors = collisionDescriptors.map((collisionDescriptor) => {
      if (collisionDescriptor instanceof OBBCollisionDescriptor) {
        return new OBBCollisionDescriptor(
          undefined,
          collisionDescriptor.type,
          new TransformationData(
            undefined,
            [
              collisionDescriptor.transformationData.position.x,
              collisionDescriptor.transformationData.position.y,
              collisionDescriptor.transformationData.position.z,
            ],
            [
              collisionDescriptor.transformationData.rotation.x,
              collisionDescriptor.transformationData.rotation.y,
              collisionDescriptor.transformationData.rotation.z,
            ],
          ),
          collisionDescriptor.halfSize.clone().addScalar(SCALAR_SMALLER_BOX),
          collisionDescriptor.usageTypes,
        );
      }

      return collisionDescriptor;
    });
    this._receiptSettings = receiptSettings;

    this._rotateStep = rotateStep;
    this._possibleRotations = possibleRotations;
    this._builderName = builderName;
    if (jsonData) {
      this._jsonData = JSON.parse(jsonData);
    }
    this._utilityIds = utilityIds;
    if (parameters) {
      this._parameters = JSON.parse(parameters);
    }
    this._status = status;
    this._productBlueprintVisibility = productBlueprintVisibility;
  }

  private _generateDefaultColisionDescriptor(model: Object3D) {
    if (!GENERATE_DEFAULT_COLISION_DESCRIPTOR) return;
    this._collisionDescriptors.filter((colisionDescriptor) => colisionDescriptor !== this._defaultColisionDescriptor);
    this._defaultColisionDescriptor = undefined;
    const box = new Box3().setFromObject(model);

    const center = new Vector3();
    box.getCenter(center);

    const transformationData = new TransformationData(undefined, [center.x, center.y, center.z], [0, 0, 0]);
    const boxSize = new Vector3();
    box.getSize(boxSize);
    const halfSize = boxSize.divideScalar(2);
    const colisionDescriptor = new OBBCollisionDescriptor(
      undefined,
      CollisionDescriptorType.OBB,
      transformationData,
      halfSize,
      [CollisionDescriptorUsageType.DEFAULT],
    );
    this._defaultColisionDescriptor = colisionDescriptor;
    this._collisionDescriptors.push(colisionDescriptor);
  }

  private _setBoundingBox(loaded: Object3D): void {
    this._selfBoundingBox = this._boundingBox = new Box3().setFromObject(loaded);
    if (!this.collisionDescriptors || this.collisionDescriptors.length === 0) {
      this._boundingBox = new Box3().setFromObject(loaded);
    } else {
      this._boundingBox = colisionDescriptorsToBoundingBox(this._collisionDescriptors);
    }
  }

  private _containsAttributeValue(referenceValueToCheck: AttributeValue, attributes: Attribute[]) {
    for (const attribute of attributes) {
      for (const attributeValue of attribute.attributeValues) {
        if (attributeValue.referenceValues.includes(referenceValueToCheck)) {
          return true;
        }

        if (this._containsAttributeValue(referenceValueToCheck, attributeValue.attributes)) {
          return true;
        }
      }
    }

    return false;
  }

  public clone(): ProductBlueprint {
    return new ProductBlueprint(
      this.id,
      this._name,
      this._dimensions,
      this.modelUrl,
      this.thumbnailUrl,
      this._infoText,
      this.attributes,
      this.productRelationshipGroups,
      this.externalId,
      this._flippingAxis,
      this._basePrice,
      this._movementData,
      this._tlorisPlaceholderIcon,
      this._collisionDescriptors,
      this._rotateStep,
      this._possibleRotations,
      this._builderName,
      JSON.stringify(this._jsonData),
      this._utilityIds,
      JSON.stringify(this._parameters),
    );
  }

  public loadNewModel(model$: Observable<GLTF>) {
    this._modelLoadingSubscription = model$.pipe(first()).subscribe((gltf) => {
      this._setBoundingBox(gltf.scene);
      this._generateDefaultColisionDescriptor(gltf.scene);
      this._smallerCollisionDescriptors = this._collisionDescriptors.map((collisionDescriptor) => {
        if (collisionDescriptor instanceof OBBCollisionDescriptor) {
          return new OBBCollisionDescriptor(
            undefined,
            collisionDescriptor.type,
            new TransformationData(
              undefined,
              [
                collisionDescriptor.transformationData.position.x,
                collisionDescriptor.transformationData.position.y,
                collisionDescriptor.transformationData.position.z,
              ],
              [
                collisionDescriptor.transformationData.rotation.x,
                collisionDescriptor.transformationData.rotation.y,
                collisionDescriptor.transformationData.rotation.z,
              ],
            ),
            collisionDescriptor.halfSize.clone().addScalar(SCALAR_SMALLER_BOX),
            collisionDescriptor.usageTypes,
          );
        }
        return collisionDescriptor;
      });
      if (!this._boundingBox) {
        throw new Error('Bounding box failed to calculate');
      }

      this._boundingBox.getSize(this._sizeVector);
      const center = new Vector3();
      const height = new Vector3();
      this._boundingBox.getSize(height);
      this._boundingBox.getCenter(center);
      height.setX(0).setZ(0);
      this._centerOffset = new Vector3().subVectors(gltf.scene.position, center) /* .sub(height) */;
      if (this._model.value) {
        removeObjectsWithChildren(this._model.value);
      }
      this._model.next(gltf.scene);
    });
  }

  public populateProductBlueprintReferences(availibleBlueprints: ProductBlueprint[], idMapper: IdMapper) {
    for (const attribute of this._attributes) {
      attribute.populateProductBlueprintReferences(availibleBlueprints, idMapper);
    }

    for (const productRelationshipGroup of this._productRelationshipGroups) {
      productRelationshipGroup.populateProductBlueprintReferences(availibleBlueprints, idMapper);
    }
    for (const attribute of this._attributes) {
      attribute.populateWithAttributes(availibleBlueprints, this, idMapper);
    }
  }

  public containsAttributeValue(referenceValue: AttributeValue): boolean {
    return this._containsAttributeValue(referenceValue, this._attributes);
  }

  public toDTO() {
    return new ProductBlueprintDTO(
      Number(this.id),
      this.name.map((name) => name.toDTO()),
      this.dimensions === null ? [] : this.dimensions,
      this.modelUrl,
      this.thumbnailUrl,
      this.infoText.map((infoText) => infoText.toDTO()),
      this.attributes.map((attribute) => attribute.toDTO()),
      this.productRelationshipGroups.map((productRelationshipGroup) => productRelationshipGroup.toDTO()),
      this.externalId,
      this.flippingAxis,
      this.basePrice,
      this.movementData.toDTO(),
      this.tlorisPlaceholderIcon,
      this.collisionDescriptors.map((collisionDescriptor) => collisionDescriptor.toDTO()),
      this.rotateStep,
      this.possibleRotations,
      this.builderName,
      JSON.stringify(this.jsonData),
      this.utilityIds ? this.utilityIds.map((utilityId) => Number(utilityId)) : undefined,
      JSON.stringify(this.parameters),
      this._status,
      this._receiptSettings,
      this.productBlueprintVisibility,
    );
  }

  public dispose() {
    this._modelLoadingSubscription?.unsubscribe();
    if (this._model.value) {
      removeObjectsWithChildren(this._model.value);
    }
  }

  /* private onGlbModelLoad(loaded: GLTF): void {
    this._model = loaded.scene as Object3D;
  } */
}
